import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import leavePolicyApi, {
  AllowanceKey,
  LeaveType,
  LeaveTypeInput,
} from '../../../network/leavePolicy';
import { useToast } from '../../../utils/useToast';
import { Button } from '../../../ui/Button';

const BUCKETS: Array<{ value: AllowanceKey; label: string }> = [
  { value: 'paid_holiday', label: 'Paid holiday' },
  { value: 'unpaid_holiday', label: 'Unpaid holiday' },
  { value: 'sick_leave', label: 'Sick leave' },
  { value: 'working_remotely', label: 'Working remotely' },
  { value: 'parental_leave', label: 'Parental leave' },
  { value: 'other_leave', label: 'Other leave' },
];

const EMPTY_FORM: Required<
  Pick<
    LeaveTypeInput,
    | 'name'
    | 'colour'
    | 'paid'
    | 'counts_against_allowance'
    | 'allowance_key'
    | 'requires_approval'
    | 'allows_part_days'
    | 'requires_note'
  >
> = {
  name: '',
  colour: '#64748b',
  paid: true,
  counts_against_allowance: false,
  allowance_key: 'paid_holiday',
  requires_approval: true,
  allows_part_days: true,
  requires_note: false,
};

type FormState = typeof EMPTY_FORM;

function errorMessage(error: any, fallback: string) {
  const message = error?.response?.data?.message;
  return typeof message === 'string' && message ? message : fallback;
}

const FLAGS: Array<{ field: keyof FormState; label: string }> = [
  { field: 'paid', label: 'Paid' },
  { field: 'counts_against_allowance', label: 'Counts against allowance' },
  { field: 'requires_approval', label: 'Requires approval' },
  { field: 'allows_part_days', label: 'Allows half days' },
  { field: 'requires_note', label: 'Requires a note or attachment' },
];

/**
 * Settings > Leave types: the types people can book. Existing companies
 * start with the six built-in types. Deactivating a type hides it from
 * booking; its requests keep their history.
 */
export function LeaveTypesSettings() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [editing, setEditing] = useState<LeaveType | 'new' | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const { data: types = [], isLoading, isError } = useQuery(
    ['leave-types', true],
    () => leavePolicyApi.getLeaveTypes(true),
    { retry: false },
  );

  const refresh = () => queryClient.invalidateQueries('leave-types');

  const save = useMutation(
    async (values: FormState) => {
      const input: LeaveTypeInput = {
        ...values,
        allowance_key: values.counts_against_allowance
          ? values.allowance_key
          : null,
      };
      if (editing && editing !== 'new' && editing.id) {
        return leavePolicyApi.updateLeaveType(editing.id, input);
      }
      return leavePolicyApi.createLeaveType(input);
    },
    {
      onSuccess: () => {
        toast({ title: 'Leave type saved', variant: 'success' });
        setEditing(null);
        refresh();
      },
      onError: (error) =>
        void toast({
          title: 'Leave type not saved',
          description: errorMessage(error, 'Please try again.'),
          variant: 'error',
        }),
    },
  );

  const toggleActive = useMutation(
    (type: LeaveType) =>
      leavePolicyApi.updateLeaveType(type.id as string, { active: !type.active }),
    {
      onSuccess: (_, type) => {
        toast({
          title: type.active ? 'Leave type deactivated' : 'Leave type activated',
          description: type.active
            ? `${type.name} can no longer be booked. Existing requests are kept.`
            : `${type.name} can be booked again.`,
          variant: 'success',
        });
        refresh();
      },
      onError: (error) =>
        void toast({
          title: 'Could not update leave type',
          description: errorMessage(error, 'Please try again.'),
          variant: 'error',
        }),
    },
  );

  const remove = useMutation(
    (type: LeaveType) => leavePolicyApi.deleteLeaveType(type.id as string),
    {
      onSuccess: () => {
        toast({ title: 'Leave type deleted', variant: 'success' });
        refresh();
      },
      onError: (error) =>
        void toast({
          title: 'Leave type not deleted',
          description: errorMessage(error, 'Deactivate it instead.'),
          variant: 'error',
        }),
    },
  );

  const startEdit = (type: LeaveType | 'new') => {
    setEditing(type);
    setForm(
      type === 'new'
        ? EMPTY_FORM
        : {
            name: type.name,
            colour: type.colour,
            paid: type.paid,
            counts_against_allowance: type.counts_against_allowance,
            allowance_key: type.allowance_key ?? 'paid_holiday',
            requires_approval: type.requires_approval,
            allows_part_days: type.allows_part_days,
            requires_note: type.requires_note,
          },
    );
  };

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.name.trim()) {
      toast({ title: 'Name is required', variant: 'error' });
      return;
    }
    save.mutate({ ...form, name: form.name.trim() });
  };

  if (isLoading) return <p className="text-sm text-gray-500">Loading leave types...</p>;
  if (isError) {
    return (
      <p role="alert" className="text-sm text-red-600">
        Leave types could not be loaded.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs font-semibold uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Allowance</th>
              <th className="px-3 py-2">Rules</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {types.map((type) => (
              <tr key={type.key} className={type.active ? '' : 'text-gray-400'}>
                <td className="px-3 py-2">
                  <span className="flex items-center gap-2">
                    <span
                      aria-hidden="true"
                      className="inline-block h-3 w-3 rounded-full"
                      style={{ backgroundColor: type.colour }}
                    />
                    {type.name}
                    {!type.paid && (
                      <span className="rounded bg-gray-100 px-1.5 text-xs text-gray-600">
                        Unpaid
                      </span>
                    )}
                  </span>
                </td>
                <td className="px-3 py-2">
                  {type.counts_against_allowance
                    ? BUCKETS.find((b) => b.value === type.allowance_key)?.label
                    : 'Not counted'}
                </td>
                <td className="px-3 py-2 text-xs text-gray-600">
                  {[
                    type.requires_approval ? 'Approval' : 'Auto-approved',
                    type.allows_part_days ? 'Half days' : 'Whole days',
                    type.requires_note ? 'Note required' : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </td>
                <td className="px-3 py-2">{type.active ? 'Active' : 'Inactive'}</td>
                <td className="whitespace-nowrap px-3 py-2 text-right">
                  <Button size="xs" variant="ghost" onClick={() => startEdit(type)}>
                    Edit
                  </Button>
                  <Button
                    size="xs"
                    variant="ghost"
                    onClick={() => toggleActive.mutate(type)}
                    disabled={toggleActive.isLoading}
                  >
                    {type.active ? 'Deactivate' : 'Activate'}
                  </Button>
                  {!type.is_default && (
                    <Button
                      size="xs"
                      variant="ghost"
                      color="red"
                      onClick={() => {
                        if (window.confirm(`Delete ${type.name}?`)) remove.mutate(type);
                      }}
                    >
                      Delete
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing ? (
        <form
          onSubmit={onSubmit}
          aria-label={editing === 'new' ? 'New leave type' : `Edit ${editing.name}`}
          className="flex flex-col gap-4 rounded-lg border border-gray-200 p-4"
        >
          <h3 className="text-sm font-semibold">
            {editing === 'new' ? 'New leave type' : `Edit ${editing.name}`}
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_auto]">
            <label className="flex flex-col gap-1 text-sm">
              Name
              <input
                className="rounded-md border border-gray-300 px-3 py-2"
                value={form.name}
                maxLength={60}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Colour
              <input
                type="color"
                className="h-10 w-16 rounded-md border border-gray-300"
                value={form.colour}
                onChange={(e) => setForm({ ...form, colour: e.target.value })}
              />
            </label>
          </div>
          <fieldset className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <legend className="sr-only">Rules</legend>
            {FLAGS.map(({ field, label }) => (
              <label key={field} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={Boolean(form[field])}
                  onChange={(e) => setForm({ ...form, [field]: e.target.checked })}
                />
                {label}
              </label>
            ))}
          </fieldset>
          {form.counts_against_allowance && (
            <label className="flex flex-col gap-1 text-sm">
              Deducts from
              <select
                className="rounded-md border border-gray-300 px-3 py-2"
                value={form.allowance_key ?? 'paid_holiday'}
                onChange={(e) =>
                  setForm({ ...form, allowance_key: e.target.value as AllowanceKey })
                }
              >
                {BUCKETS.map((bucket) => (
                  <option key={bucket.value} value={bucket.value}>
                    {bucket.label}
                  </option>
                ))}
              </select>
            </label>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button type="submit" color="red" disabled={save.isLoading}>
              {save.isLoading ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </form>
      ) : (
        <div>
          <Button color="red" onClick={() => startEdit('new')}>
            Add leave type
          </Button>
        </div>
      )}
    </div>
  );
}
